import type { PortalAccessLevel } from '@statura/types';
import {
  STATURA_CARE_PRIVACY_VERSION,
  STATURA_CARE_PUBLIC_URLS,
  STATURA_CARE_REPRESENTATIVE_AUTHORITY_VERSION,
  STATURA_CARE_TERMS_VERSION,
} from './public-legal';

export type PortalLegalDocumentType =
  | 'portal_terms'
  | 'portal_privacy'
  | 'representative_authority';

export interface PortalLegalDocumentDefinition {
  type: PortalLegalDocumentType;
  label: string;
  version: string;
  url: string | null;
  appliesTo: readonly PortalAccessLevel[];
  acknowledgementLabel: string;
  summary: string;
}

export type PortalLegalAcceptanceAuditRecord = {
  documentType: PortalLegalDocumentType;
  version: string;
};

const NOMINEE_RELATIONSHIP_RE =
  /\b(nominee|representative|guardian|attorney|decision[ -]?maker|administrator|poa|epa)\b/i;
const CLIENT_RELATIONSHIP_RE = /^(self|client|consumer|resident|participant)$/i;

export const PORTAL_LEGAL_DOCUMENT_DEFINITIONS: readonly PortalLegalDocumentDefinition[] = [
  {
    type: 'portal_terms',
    label: 'Client portal terms of use',
    version: STATURA_CARE_TERMS_VERSION,
    url: STATURA_CARE_PUBLIC_URLS.terms,
    appliesTo: ['CLIENT', 'NOMINEE', 'CONTACT_PERSON'],
    acknowledgementLabel:
      'I have reviewed the Statura Care client portal terms of use and agree to continue using the portal under those terms.',
    summary:
      'Confirms continued use of the Statura Care client and family portal under the current terms of use.',
  },
  {
    type: 'portal_privacy',
    label: 'Privacy policy',
    version: STATURA_CARE_PRIVACY_VERSION,
    url: STATURA_CARE_PUBLIC_URLS.privacy,
    appliesTo: ['CLIENT', 'NOMINEE', 'CONTACT_PERSON'],
    acknowledgementLabel:
      'I have reviewed the current Statura Care privacy policy and understand how portal information is handled.',
    summary:
      'Confirms review of the current Statura Care privacy policy for portal access and support.',
  },
  {
    type: 'representative_authority',
    label: 'Representative authority attestation',
    version: STATURA_CARE_REPRESENTATIVE_AUTHORITY_VERSION,
    url: null,
    appliesTo: ['NOMINEE', 'CONTACT_PERSON'],
    acknowledgementLabel:
      'I confirm that I am authorised by the client, or otherwise lawfully authorised, to access this portal and act in connection with the client’s care information.',
    summary:
      'Confirms that a non-client portal user is authorised to access and act in connection with the client’s portal information.',
  },
] as const;

export function derivePortalAccessLevelFromRelationship(
  relationship: string | null | undefined,
): PortalAccessLevel {
  const normalized = relationship?.trim() ?? '';
  if (!normalized) return 'CONTACT_PERSON';
  if (NOMINEE_RELATIONSHIP_RE.test(normalized)) return 'NOMINEE';
  if (CLIENT_RELATIONSHIP_RE.test(normalized)) return 'CLIENT';
  return 'CONTACT_PERSON';
}

const PORTAL_ACCESS_LEVELS = new Set<PortalAccessLevel>(['CLIENT', 'NOMINEE', 'CONTACT_PERSON']);

/** Least-privileged portal tier (fail-closed default for invalid explicit values). */
export const PORTAL_ACCESS_LEVEL_LEAST_PRIVILEGED: PortalAccessLevel = 'CONTACT_PERSON';

/**
 * Source label for how the access tier was resolved.
 * Must stay consistent with SQL access_level_source in get_portal_access_context_controlled.
 */
export type PortalAccessLevelSource = 'provider' | 'heuristic_default' | 'invalid_state';

/**
 * Resolve portal access tier (fail-closed):
 * - NULL/undefined explicit → relationship-regex heuristic
 * - valid enum → that tier
 * - any other non-null explicit → CONTACT_PERSON (least privilege); never elevate via heuristic
 */
export function resolvePortalAccessLevel(input: {
  explicit?: string | null;
  relationship?: string | null;
}): PortalAccessLevel {
  // Only true null/undefined invokes the heuristic (matches SQL `p_explicit IS NULL`).
  if (input.explicit == null) {
    return derivePortalAccessLevelFromRelationship(input.relationship);
  }
  const explicit = input.explicit.trim().toUpperCase();
  if (PORTAL_ACCESS_LEVELS.has(explicit as PortalAccessLevel)) {
    return explicit as PortalAccessLevel;
  }
  return PORTAL_ACCESS_LEVEL_LEAST_PRIVILEGED;
}

export function portalAccessLevelSource(
  explicit: string | null | undefined,
): PortalAccessLevelSource {
  if (explicit == null) return 'heuristic_default';
  const value = explicit.trim().toUpperCase();
  if (PORTAL_ACCESS_LEVELS.has(value as PortalAccessLevel)) return 'provider';
  return 'invalid_state';
}

export function getPortalLegalDocumentDefinition(
  documentType: PortalLegalDocumentType,
): PortalLegalDocumentDefinition {
  const definition = PORTAL_LEGAL_DOCUMENT_DEFINITIONS.find((item) => item.type === documentType);
  if (!definition) {
    throw new Error(`Unknown portal legal document type: ${documentType}`);
  }
  return definition;
}

export function getRequiredPortalLegalDocuments(
  accessLevel: PortalAccessLevel,
): PortalLegalDocumentDefinition[] {
  return PORTAL_LEGAL_DOCUMENT_DEFINITIONS.filter((definition) =>
    definition.appliesTo.includes(accessLevel),
  );
}

export function buildPortalLegalAcceptanceAction(
  documentType: PortalLegalDocumentType,
  version: string,
): string {
  return `LEGAL_ACCEPTANCE:${documentType}:${version}`;
}

export function parsePortalLegalAcceptanceAction(
  action: string | null | undefined,
): PortalLegalAcceptanceAuditRecord | null {
  if (!action) return null;

  const match =
    /^LEGAL_ACCEPTANCE:(portal_terms|portal_privacy|representative_authority):(.+)$/.exec(action);
  if (!match) return null;
  const version = match[2];
  if (!version) return null;

  return {
    documentType: match[1] as PortalLegalDocumentType,
    version,
  };
}
